import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import TopNav from "./TopNav";
import AccountsSidebar from "./AccountsSidebar";
import RightSidebar from "./RightSidebar";
import MainContent from "./MainContent";
import GlobalDropZone from "../GlobalDropZone";
import CreateAccountModal from "../CreateAccountModal";

const NewDashboardLayout = ({
  accounts,
  loading,
  onAccountCreated,
  onRefresh,
}) => {
  const { accountId: accountIdParam, tab, symbol } = useParams();
  const [searchParams] = useSearchParams();
  const accountIdFromQuery = searchParams.get('accountId');
  const [refreshingAccountId, setRefreshingAccountId] = React.useState(null);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  const modalDismissed = React.useRef(false);

  // Initialize selectedAccountId from URL params or query string (null means "All Accounts")
  // For ticker routes, accountId comes from query param; for account routes, it comes from path param
  const selectedAccountId = accountIdParam || accountIdFromQuery || null;

  // Get active tab from URL or default to "summary"
  const activeTab = tab || "summary";

  // Show Add Account modal when user has no accounts (after loading completes)
  // Only show once - don't reopen if user manually dismissed it
  React.useEffect(() => {
    if (
      !loading &&
      accounts.length === 0 &&
      !showCreateModal &&
      !modalDismissed.current
    ) {
      setShowCreateModal(true);
    }
  }, [loading, accounts.length]);

  // Track whether we were previously syncing so we can fire a final refresh
  // when sync_in_progress transitions true → false.
  const wasSyncingRef = React.useRef(false);

  // Auto-refresh when any account is syncing
  React.useEffect(() => {
    const hasSyncInProgress = accounts.some((acc) => acc.sync_in_progress);

    // Sync just completed — fire one final refresh so the page shows fresh data.
    // Without this, the last polling cycle's refreshTrigger fired while sync was
    // still running, so SummaryTab / HoldingsTable would have stale values.
    if (wasSyncingRef.current && !hasSyncInProgress) {
      wasSyncingRef.current = false;
      onRefresh();
      setRefreshTrigger((t) => t + 1);
      return;
    }

    wasSyncingRef.current = hasSyncInProgress;

    if (!hasSyncInProgress) {
      return; // No syncing accounts, no need to poll
    }

    // Poll every 3 seconds while any account is syncing. Only the accounts list is
    // refreshed here (that is what drives the sidebar's sync spinners). Do NOT bump
    // refreshTrigger: SummaryTab/HoldingsTable/ActivityTable/the chart all show a
    // full-body spinner on every trigger change, so bumping it here made the main
    // page reload every 3s until the last sync finished. The body is refreshed once,
    // above, when sync_in_progress transitions true → false.
    const intervalId = setInterval(() => {
      onRefresh();
    }, 3000);

    // Cleanup interval when component unmounts or when no more syncing
    return () => {
      clearInterval(intervalId);
    };
  }, [accounts, onRefresh]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Top Navigation */}
      <TopNav selectedAccountId={selectedAccountId} />

      {/* Main Layout: Sidebar + Content + Right Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <AccountsSidebar
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          activeTab={activeTab}
          onAccountCreated={onAccountCreated}
          onRefresh={onRefresh}
          refreshingAccountId={refreshingAccountId}
          refreshTrigger={refreshTrigger}
          onSyncComplete={() => {
            onRefresh();
            setRefreshTrigger((t) => t + 1);
          }}
        />

        {/* Main Content */}
        <MainContent
          selectedAccountId={selectedAccountId}
          activeTab={activeTab}
          symbol={symbol}
          accounts={accounts}
          onImportComplete={onRefresh}
          onRefresh={onRefresh}
          refreshingAccountId={refreshingAccountId}
          setRefreshingAccountId={setRefreshingAccountId}
          refreshTrigger={refreshTrigger}
          setRefreshTrigger={setRefreshTrigger}
          onAddAccount={() => setShowCreateModal(true)}
        />

        {/* Right Sidebar - Alerts */}
        <RightSidebar />
      </div>

      {/* Global dropzone for CSV and images */}
      <GlobalDropZone
        accounts={accounts}
        currentAccountId={selectedAccountId}
        onUploadComplete={() => {
          setRefreshTrigger((prev) => prev + 1); // Trigger child components to refresh
          onRefresh(); // Refresh accounts list
        }}
      />

      {/* Show Create Account Modal when user has no accounts */}
      {showCreateModal && (
        <CreateAccountModal
          onClose={() => {
            setShowCreateModal(false);
            modalDismissed.current = true; // Mark as dismissed so it won't reopen
          }}
          onCreate={onAccountCreated}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
};

export default NewDashboardLayout;
